import { usePages as useArtisPages, usePagesReady } from "@artis/sdk-react";
import { lazy, Suspense, useEffect } from "react";

import { usePages } from "../../data";
import { useUserId } from "../../context/auth/hooks";
import Loading from "../../webapp/components/Loading";
import { useArtisPagesEnabled } from "../../webapp/market-pages/artis-pages/enabled";
import { publishSoleGridPaneId, usePaneActivePageId, usePaneId } from "../../webapp/pane/context";

export const MARKET_GRID_MODULE = "market-grid";

const MarketPage = lazy(() => import("../../webapp/MarketView").then((m) => ({ default: m.MarketPage })));

export default function MarketGridModuleView() {
  const artisPages = useArtisPagesEnabled();
  return artisPages ? <ArtisMarketGridModule /> : <LegacyMarketGridModule />;
}

/**
 * A pane keeps its page in local storage, so it outlives the page itself: delete the
 * page you are looking at (or open a session whose stored page is long gone) and the
 * pane would hold a dead id, leaving MarketPage on its loading state forever, because
 * it only navigates away from a missing page when the route supplied the id.
 * Reseed once the page list is known and does not contain it.
 */
function MarketGridPane({ seedPageId, pageMissing }: { seedPageId: string | undefined; pageMissing: boolean }) {
  const pane = usePaneActivePageId();
  const pageId = pane?.[0];
  const setPageId = pane?.[1];
  const paneId = usePaneId();

  useEffect(() => {
    if (!setPageId || !seedPageId) return;
    if (!pageId || pageMissing) setPageId(seedPageId);
  }, [pageId, pageMissing, seedPageId, setPageId]);

  // TODO! REMOVE WITH the single-grid shim in webapp/pane/context.tsx.
  useEffect(() => publishSoleGridPaneId(paneId), [paneId]);

  if (!pageId) return <Loading showLogo syncing={false} />;

  return (
    <Suspense fallback={<Loading showLogo syncing={false} />}>
      <MarketPage pageId={pageId} />
    </Suspense>
  );
}

function LegacyMarketGridModule() {
  const userId = useUserId();
  const pages = usePages(userId);
  const pageId = usePaneActivePageId()?.[0];
  const pageMissing = !!pages.data && !!pageId && !pages.data.some((page) => page.id === pageId);
  return <MarketGridPane seedPageId={pages.data?.[0]?.id} pageMissing={pageMissing} />;
}

/** The SDK list is the one create and duplicate update at once. The legacy query lags that write. */
function ArtisMarketGridModule() {
  const pages = useArtisPages();
  const ready = usePagesReady();
  const pageId = usePaneActivePageId()?.[0];
  const pageMissing = ready && !!pageId && !pages.some((page) => page.id === pageId);
  return <MarketGridPane seedPageId={ready ? pages[0]?.id : undefined} pageMissing={pageMissing} />;
}
